import React, { useEffect, useState } from 'react';
import MindUpAdminMobileUserReservationConsultationHeader from './MindUpAdminMobileUserReservationConsultationHeader';
import styled from 'styled-components';
import { useDiagnosisListByStatusQuery } from 'api/Queries/Diagnosis/useDiagnosisListByStatusQuery';
import { AccordionList } from 'components/Accordion/AccordionList';
import { useLoginStore } from 'store/useLoginDataStore';
import { addNoShowDisabledField } from 'pages/Admin/MindUp/utils/mindUpAdminFunctions';
import { IDDiagnosis, IDDiagnosisNull } from 'models/DCK/basicInterface';
import 'moment/locale/ko'; // 한국어 로케일 설정
import { BorderColor } from 'styles/Global/colorSemantic';
import { Reschedule, Standby } from 'components/CounselingCards/CounselingCards';
import useToggle from 'hooks/CustomHooks/useToggle';
import ConfirmDialogueListLabelHorizonLeft from 'components/DesignSystem/MobileConfirmDialogue/ConfirmDialogueListLabelHorizonLeft';
import { TodaydiagnosisSliderData } from 'models/Hospital/todayDiagnosisPageTypes';
import { DiagnosisStatus } from 'utils/statusCode';
import useDiagnosisChangeStatusMutation from 'api/Mutations/Diagnosis/useDiagnosisChangeStatusMutation';
import { NothingDataInMindUpAdminMobileUserReservationConsultationPage } from 'pages/Admin/DCK/AddDiagnosisPage/NothingDataInPagination';
import { formatDateDay } from 'utils/calculateTime';
import { getDiagnosisStatus } from 'pages/DCK/utils';
import { MobileAlertDialogPCInfo } from 'components/DesignSystem/MobileAlertDialog/MobileAlertDialog';
import { useSnackBarStore } from 'store/useToastStore';
import { useNavigate } from 'react-router-dom';
import { mindupAdminMobilePaths } from 'constants/path';
import MindUpAdminMobileMenu from 'components/Menu/MindUpAdminMobileMenu';

// 그룹화된 진료 데이터를 위한 인터페이스
interface IGroupedDiagnosisItem {
    dateTimeDisplay: string;
    items: IDDiagnosis[];
    visible?: boolean;
}

const MindUpAdminMobileUserReservationConsultationPage = () => {
    const navigate = useNavigate();
    // const [initData, setInitData] = useState<IDDiagnosis[]>();
    const [groupedDiagnosis, setGroupedDiagnosis] = useState<IGroupedDiagnosisItem[]>([]);
    const [diagnosisInfo, setDiagnosisInfo] = useState<IDDiagnosisNull>();

    const [onConfirmModal, setOnConfirmModal] = useToggle();
    const [onInfoModal, setOnInfoModal] = useToggle();
    const [onMenu, setOnMenu] = useToggle();

    const { data: diagnosisListData, isLoading: diagnosisListLoading, refetch: refetchDiagnosisListData } = useDiagnosisListByStatusQuery({ status: [4] });
    const { mutate: diagnosisChangeStatusMutation } = useDiagnosisChangeStatusMutation();

    const { loginData } = useLoginStore();
    const { showSnackBar, hideSnackBar } = useSnackBarStore();

    // Data Fetching
    useEffect(() => {
        if (!diagnosisListLoading && diagnosisListData) {
            processDiagnosisData();
        }
    }, [diagnosisListData]);

    // 데이터 처리 함수
    const processDiagnosisData = () => {
        if (!diagnosisListData || diagnosisListData?.length === 0) return;

        let sortedData = diagnosisListData.filter((data) => data.doctorId === loginData.id).sort((a, b) => b.diagnosisDateTime.localeCompare(a.diagnosisDateTime));

        sortedData = addNoShowDisabledField(sortedData);
        // setInitData(sortedData);

        // 날짜/시간별로 그룹화
        const groupByDateTime: { [key: string]: IGroupedDiagnosisItem } = {};

        sortedData.forEach((diagnosis) => {
            const dateTimeDisplay = formatDateDay(diagnosis.diagnosisDateTime);

            // 그룹이 없으면 생성, 있으면 항목 추가
            if (!groupByDateTime[dateTimeDisplay]) {
                groupByDateTime[dateTimeDisplay] = {
                    dateTimeDisplay,
                    items: [diagnosis],
                    visible: false,
                };
            } else {
                groupByDateTime[dateTimeDisplay].items.push(diagnosis);
            }
        });

        // 객체를 배열로 변환하고 날짜순으로 정렬
        const groupedArray = Object.values(groupByDateTime);

        if (groupedArray.length > 0) {
            groupedArray.forEach((group) => {
                group.items.sort((a, b) => {
                    return a.diagnosisDateTime.localeCompare(b.diagnosisDateTime);
                });
            });

            groupedArray.sort((a, b) => {
                // 원본 날짜 형식으로 비교하기 위해 첫 번째 항목의 diagnosisDateTime을 사용
                const dateA = a.items[0]?.diagnosisDateTime || '';
                const dateB = b.items[0]?.diagnosisDateTime || '';
                return dateA.localeCompare(dateB);
            });
            groupedArray[0].visible = true;
        }

        setGroupedDiagnosis(groupedArray);
    };

    // 아코디언 토글 핸들러
    const handleAccordionToggle = (index: number) => {
        setGroupedDiagnosis((prev) =>
            prev.map((group, i) => {
                if (i === index) {
                    return { ...group, visible: !group.visible };
                }
                return group;
            })
        );
    };

    const processCheckBarClick = (item: TodaydiagnosisSliderData) => {
        // 새로 상담 일시 잡기
        // if (item.noShowDisabled && onChangeDiagnosisDateModal) {
        //         onChangeDiagnosisDateModal(item);
        //     }
        //    상담 완료

        if (getDiagnosisStatus(item) === 'reschedule') {
            setOnInfoModal();
        } else {
            setOnConfirmModal();
        }

        setDiagnosisInfo(item);
    };

    const clickCancelButton = () => {
        setOnConfirmModal();
        setDiagnosisInfo(undefined);
    };

    // 상담 완료 함수
    const handleCounselComplete = async () => {
        if (!diagnosisInfo?._id) {
            return;
        }

        const changeStatusRqData = {
            _id: diagnosisInfo._id,
            status: DiagnosisStatus.counselComplete,
        };

        diagnosisChangeStatusMutation(changeStatusRqData, {
            onSuccess: (data) => {
                localStorage.setItem('snackbar', 'true');
                // clickCancelButton();
                // refetchDiagnosisListData();
                showSnackBar('상담이 완료 처리 되었습니다!', 'success');
                navigate(mindupAdminMobilePaths.mindUpAdminMobileDetailDiagnosis, { state: data });
            },
            onError: (error) => {
                console.error('상담 완료 처리 실패', error);
            },
        });
    };

    return (
        <>
            {onMenu && <MindUpAdminMobileMenu close={setOnMenu} />}
            <MindUpAdminMobileUserReservationConsultationHeader onClick={setOnMenu} />
            <Layout>
                {onInfoModal && (
                    <MobileAlertDialogPCInfo
                        firstTitle='현재는 PC버전에서만'
                        secondTitle='상담 일시 변경이 가능합니다.'
                        firstContent='불편을 드려 죄송합니다.'
                        secondContent='아래의 상담사 사이트를 PC로 접속하여 노쇼 처리된 상담 일정을 변경 부탁드립니다.'
                        info='http://tlcdoctors.co.kr'
                        button='확인'
                        clickButton={setOnInfoModal}
                    />
                )}
                {onConfirmModal && (
                    <ConfirmDialogueListLabelHorizonLeft
                        title='상담을 완료하셨나요?'
                        content='상담을 끝내셨다면, 완료 처리해주세요.'
                        diagnosisInfo={diagnosisInfo}
                        firstButton='취소'
                        secondButton='상담 완료'
                        clickFirstButton={clickCancelButton}
                        clickSecondButton={handleCounselComplete}
                    />
                )}
                <>
                    {groupedDiagnosis.length === 0 ? (
                        <NothingDataInMindUpAdminMobileUserReservationConsultationPage />
                    ) : (
                        groupedDiagnosis.map((group, index) => (
                            <AccordionListContainer key={index}>
                                <AccordionList date={group.dateTimeDisplay} number={group.items.length} onClick={() => handleAccordionToggle(index)} selected={group.visible} />
                                {group.visible &&
                                    group.items.map((item, itemIndex) =>
                                        getDiagnosisStatus(item) === 'reschedule' ? (
                                            <Reschedule key={itemIndex} diagnosisInfo={item} processCheckBarClick={processCheckBarClick} />
                                        ) : (
                                            <Standby key={itemIndex} diagnosisInfo={item} processCheckBarClick={processCheckBarClick} />
                                        )
                                    )}
                            </AccordionListContainer>
                        ))
                    )}
                    {groupedDiagnosis.length !== 0 && <BottomSpace />}
                </>
            </Layout>
        </>
    );
};

export default MindUpAdminMobileUserReservationConsultationPage;

const Layout = styled.section`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    min-height: 100vh;
    height: auto;
    padding-top: 56px;
`;

const AccordionListContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 14px;
    padding: 19px 16px;
    border-bottom: 1px solid ${BorderColor.color_border_weak};
    &:last-child {
        margin-bottom: 56px;
    }
`;

const BottomSpace = styled.div`
    width: 100%;
    height: 56px;
    flex-shrink: 0;
`;
